import type { LifeAreaTag } from '@contracts';
import { HeartPulse, Users, Hammer, PartyPopper, HandHeart } from '@ui/shared/icons';

/**
 * `LifeAreaTag` icon/accent set (R8.3, PRD §3/§6 new enum,
 * design/screens/today-desktop-v2.md SS8) — a THIRD, independent enum,
 * deliberately not visually matched to `HabitCategory` or `ObjectiveCategory`
 * (phase spec §3's "why not unify" ruling). Mirrors `CATEGORY_OPTIONS`/
 * `CategoryPillSelect`'s exact shape (`objective-form/FormPrimitives.tsx`),
 * added alongside rather than duplicated into a new pattern, but owns its own
 * values/tokens (`--accent-life-*`, additive to tokens.css/tokens.json).
 */
export const LIFE_AREA_OPTIONS: ReadonlyArray<{
  readonly value: LifeAreaTag;
  readonly label: string;
  readonly icon: typeof HeartPulse;
  readonly tokenPrefix: string;
}> = [
  { value: 'health', label: 'Health', icon: HeartPulse, tokenPrefix: '--accent-life-health' },
  { value: 'relationship', label: 'Relationship', icon: Users, tokenPrefix: '--accent-life-relationship' },
  { value: 'craft', label: 'Craft', icon: Hammer, tokenPrefix: '--accent-life-craft' },
  { value: 'fun', label: 'Fun', icon: PartyPopper, tokenPrefix: '--accent-life-fun' },
  { value: 'contribution', label: 'Contribution', icon: HandHeart, tokenPrefix: '--accent-life-contribution' },
];

/**
 * Multi-select tag row (0-5 selections, AC-T9) — `role="group"` with
 * individually toggleable `aria-pressed` buttons, NOT `role="radiogroup"`
 * (today-desktop-v2.md SS8: "this is a multi-select tag row"). Same
 * "tinted fill, no left border" selected-state recipe as every other
 * selector in the product.
 */
export function LifeAreaTagSelect({
  value,
  onToggle,
}: {
  readonly value: readonly LifeAreaTag[];
  readonly onToggle: (tag: LifeAreaTag) => void;
}) {
  return (
    <div role="group" aria-label="Favorite thing about today: life areas" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
      {LIFE_AREA_OPTIONS.map((option) => {
        const selected = value.includes(option.value);
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={selected}
            aria-label={`Life area: ${option.label}`}
            onClick={() => onToggle(option.value)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-3)',
              borderRadius: 'var(--radius-md)',
              border: selected ? `1px solid var(${option.tokenPrefix}-vivid)` : '1px solid var(--border-subtle)',
              background: selected
                ? `color-mix(in srgb, var(${option.tokenPrefix}-vivid) 14%, var(--surface-raised))`
                : 'var(--surface-raised)',
              color: selected ? `var(${option.tokenPrefix})` : 'var(--text-secondary)',
              fontSize: 'var(--type-caption-size)',
              fontWeight: 500,
            }}
          >
            <Icon size={16} color={selected ? `var(${option.tokenPrefix})` : 'var(--text-muted)'} aria-hidden="true" />
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** Resolves a `LifeAreaTag` to its token prefix (Favorite-moments/30D bars, SS12.3). */
export function lifeAreaTokenPrefix(tag: LifeAreaTag): string {
  return LIFE_AREA_OPTIONS.find((o) => o.value === tag)?.tokenPrefix ?? '--text-muted';
}

/** Resolves a `LifeAreaTag` to its display label (Favorite-moments/30D bars, SS12.3). */
export function lifeAreaLabel(tag: LifeAreaTag): string {
  return LIFE_AREA_OPTIONS.find((o) => o.value === tag)?.label ?? tag;
}
